import { c as _c } from "react/compiler-runtime";
import { useEngine } from '@svs/nova-poolbetting/core/frameworks/nextjs/engine';
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { Draw, DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { BettingEvent, BettingEventId, EventNumber, EventType, SportType } from '@svs/nova-poolbetting/schema/sport';
import { cn } from '@svs/nova-poolbetting/ui/utils';
import type { ReactNode } from 'react';
import { CouponLinkWrapper } from '@/components/index.browser';
import { CouponRowHeaderContainer } from './CouponRowHeaderContainer';
import { CouponRowOutcomeItem } from './CouponRowOutcomeItem';
type CouponRowProps = {
  couponIndex: CouponIndex;
  events: Draw['events'];
  sportType: SportType;
  bettingEvents: Record<BettingEventId, BettingEvent>;
  defaultEventType: EventType;
  hasMultipleCoupons: boolean | undefined;
  handleClick: ({
    eventNumber
  }: {
    eventNumber: EventNumber;
  }) => void;
  selectedCouponIndex: CouponIndex;
  drawNumber: DrawNumber;
  couponRowFooter?: ReactNode;
};

/**
 * Component that renders a coupon row with outcomes.
 *
 * @param root0 props for the component
 * @param root0.couponIndex index of the coupon to render
 * @param root0.events array of events
 * @param root0.handleClick function to handle click events on outcomes to show statistics
 * @param root0.sportType sport type for the events
 * @param root0.bettingEvents record of betting events
 * @param root0.defaultEventType fallback event type
 * @param root0.hasMultipleCoupons indicates if multiple coupons
 * @param root0.selectedCouponIndex currently selected coupon index
 * @param root0.drawNumber draw number for coupon
 * @param root0.couponRowFooter optional footer for the coupon row
 * @returns component that renders a coupon row with outcomes
 */
export function CouponRow(t0) {
  const $ = _c(57);
  const {
    couponIndex,
    events,
    handleClick,
    sportType,
    bettingEvents,
    defaultEventType,
    hasMultipleCoupons,
    selectedCouponIndex,
    drawNumber,
    couponRowFooter
  } = t0;
  const definition = useEngine();
  const product = definition.moduleEngine;
  let classNameForOutcomes;
  if ($[0] !== hasMultipleCoupons) {
    classNameForOutcomes = ["h-full"];
    if (hasMultipleCoupons) {
      classNameForOutcomes.push("lg:h-auto lg:flex-1 lg:min-h-0");
    }
    $[0] = hasMultipleCoupons;
    $[1] = classNameForOutcomes;
  } else {
    classNameForOutcomes = $[1];
  }
  let t1;
  if ($[2] !== bettingEvents || $[3] !== couponIndex || $[4] !== defaultEventType || $[5] !== drawNumber || $[6] !== events || $[7] !== handleClick || $[8] !== hasMultipleCoupons || $[9] !== product || $[10] !== selectedCouponIndex || $[11] !== sportType) {
    let t2;
    if ($[13] !== bettingEvents || $[14] !== couponIndex || $[15] !== defaultEventType || $[16] !== drawNumber || $[17] !== handleClick || $[18] !== hasMultipleCoupons || $[19] !== product || $[20] !== selectedCouponIndex || $[21] !== sportType) {
      t2 = event => {
        const eventType = event.status === "COMPLETE" ? bettingEvents[event.bettingEventId]?.eventType ?? defaultEventType : defaultEventType;
        return <CouponRowOutcomeItem key={`${event.eventNumber}-${couponIndex}`} couponIndex={couponIndex} selectedCouponIndex={selectedCouponIndex} hasMultipleCoupons={hasMultipleCoupons} event={event} eventType={eventType} sportType={sportType} drawNumber={drawNumber} product={product} eventTypeStatisticsId={event.status === "COMPLETE" ? bettingEvents[event.bettingEventId]?.eventTypeStatisticsId : undefined} handleClick={handleClick} />;
      };
      $[13] = bettingEvents;
      $[14] = couponIndex;
      $[15] = defaultEventType;
      $[16] = drawNumber;
      $[17] = handleClick;
      $[18] = hasMultipleCoupons;
      $[19] = product;
      $[20] = selectedCouponIndex;
      $[21] = sportType;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    t1 = events.map(t2);
    $[2] = bettingEvents;
    $[3] = couponIndex;
    $[4] = defaultEventType;
    $[5] = drawNumber;
    $[6] = events;
    $[7] = handleClick;
    $[8] = hasMultipleCoupons;
    $[9] = product;
    $[10] = selectedCouponIndex;
    $[11] = sportType;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const outcomes = t1;
  const hasFooter = Boolean(couponRowFooter);
  const t2 = hasFooter && "last:**:data-coupon-row-footer:rounded-br-md last:[&_[data-events-grid]>*:last-child]:rounded-br-none!";
  const t3 = hasMultipleCoupons ? "lg:min-h-0" : "";
  let t4;
  if ($[23] !== t2 || $[24] !== t3) {
    t4 = cn("relative flex h-full w-full max-w-65.5 min-w-30 flex-col **:data-style-element:rounded-none last:**:data-style-element:rounded-r-md lg:min-w-46 lg:self-stretch", t2, t3);
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const t5 = `bg-surface-elevated border-stroke-low mb-2 hidden border-l ${hasMultipleCoupons ? "lg:flex" : ""} ${hasMultipleCoupons && couponIndex === selectedCouponIndex ? "lg:bg-primary-variant" : ""}`;
  let t6;
  if ($[26] !== t5) {
    t6 = cn(t5);
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const t7 = Boolean(hasMultipleCoupons && couponIndex === selectedCouponIndex);
  let t8;
  if ($[28] !== couponIndex || $[29] !== t6 || $[30] !== t7) {
    t8 = <CouponRowHeaderContainer className={t6} couponIndex={couponIndex} isSelectedCouponIndex={t7} />;
    $[28] = couponIndex;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t10;
  let t9;
  if ($[32] !== couponIndex) {
    t9 = {
      coupon: couponIndex
    };
    t10 = {
      coupon: couponIndex
    };
    $[32] = couponIndex;
    $[33] = t10;
    $[34] = t9;
  } else {
    t10 = $[33];
    t9 = $[34];
  }
  const t11 = `hidden ${hasMultipleCoupons ? "lg:block" : ""}`;
  let t12;
  if ($[35] !== t11) {
    t12 = cn(t11);
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const t13 = `${couponIndex + 1}`;
  let t14;
  if ($[37] !== t13) {
    t14 = <span className="sr-only">Kupong {t13}</span>;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t10 || $[40] !== t12 || $[41] !== t14 || $[42] !== t9) {
    t15 = <CouponLinkWrapper defaultQuery={t9} queryOnSmallScreen={t10} shouldScrollToTop={false} className={t12}>{t14}</CouponLinkWrapper>;
    $[39] = t10;
    $[40] = t12;
    $[41] = t14;
    $[42] = t9;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = !hasFooter && "lg:pb-1";
  const t17 = classNameForOutcomes.join(" ");
  let t18;
  if ($[44] !== t16 || $[45] !== t17) {
    t18 = cn("grid auto-rows-fr gap-1 lg:gap-2", t16, t17);
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== outcomes || $[48] !== t18) {
    t19 = <div data-events-grid={true} className={t18}>{outcomes}</div>;
    $[47] = outcomes;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = couponRowFooter && couponRowFooter;
  let t21;
  if ($[50] !== couponIndex || $[51] !== t15 || $[52] !== t19 || $[53] !== t20 || $[54] !== t4 || $[55] !== t8) {
    t21 = <div key={couponIndex} data-coupon-index={couponIndex} className={t4}>{t8}{t15}{t19}{t20}</div>;
    $[50] = couponIndex;
    $[51] = t15;
    $[52] = t19;
    $[53] = t20;
    $[54] = t4;
    $[55] = t8;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  return t21;
}